import { Component, ViewChild } from '@angular/core';
import { NewSearchBasePage } from '@app/new-search/shared/new-search-base.page';
import { ModalController, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { BrowserPageTitle } from '@shared/constants/browser-page-title.enum';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { FacetFlyoutContainerComponent } from '@app/new-search/components/facet-flyout-container/facet-flyout-container.component';
import { Location } from '@angular/common';
import { ListsOverlayService } from '@app/lists/services/lists-overlay.service';
import { ListsService } from '@app/lists/services/lists.service';
import { AddToListModalService } from '@shared/services/dual-pane/add-to-list-modal.service';
import { ProductService } from '@shared/services/product/product.service';
import { SelectionType } from '@app/new-search/models/selection-type.enum';
import { UserService } from '@app/user/services';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { Params } from '@angular/router';
import { environment } from 'src/environments/environment';
import { RecipeDetailService } from '@app/mpp/pages/recipe-detail/services/recipe-detail.service';
import { buildNotifyTrigger, ProductListing } from '@coveo/headless/commerce';

@Component({
  selector: 'app-product-listing',
  templateUrl: './product-listing.page.html',
  styleUrls: ['./product-listing.page.scss'],
})
export class ProductListingPage
  extends NewSearchBasePage
  implements ViewWillEnter, ViewWillLeave
{
  @ViewChild(FacetFlyoutContainerComponent)
  facetFlyout: FacetFlyoutContainerComponent;

  footerActionText: string;
  queryParamUnsubscribe$: Subscription;
  isThreeOperationButtons: boolean;

  constructor(
    public listsOverlayService: ListsOverlayService,
    public addToListModalService: AddToListModalService,
    public listsService: ListsService,
    readonly productService: ProductService,
    public location: Location,
    readonly userService: UserService,
    protected modalController: ModalController,
    readonly recipeDetailService: RecipeDetailService,
  ) {
    super(
      listsOverlayService,
      addToListModalService,
      listsService,
      productService,
      location,
      recipeDetailService,
      userService,
    );
  }

  public goBack() {
    this.navigationHelperService.redirectionBack();
  }

  async ionViewWillEnter() {
    this.initializeSelectedCustomer();
    this.buildListFacet();
    const context = await this.coveoService.getContext();

    const path = this.location.path().split('?')[0];
    context.setView({
      url: `https://${environment.webDomain}${path}`,
    });

    this.initializeCoveo({
      initializeSearchBoxController: false,
      initializeNotificationController: true,
      pageType: 'productListing',
    });

    this.notificationControllerUnsubscribe =
      this.notificationController.subscribe(() => {
        if (this.notificationController.state.notifications.length > 0) {
          const data = this.notificationController.state.notifications[0];
          this.doUpdateTopSection(JSON.parse(data));
        }
      });
    this.newSearchViewModel = { products: [] };

    const queryParam$ = this.route.queryParams.pipe(distinctUntilChanged());
    this.isTouchExperience$ = this.platformService.isTouchExperience$.subscribe(
      isTouchExperience => {
        this.isTouchExperience = isTouchExperience;
      },
    );

    this.listPageDataSub$ = this.listsService.listsView$().subscribe(data => {
      this.listPageData = data;
    });

    this.panAppState.setPageTitle(PageTitle.shopProducts);

    this.scrollToTop();

    this.coveoClickEventSub$ =
      this.coveoService.searchResultClickEvent$?.subscribe(value => {
        const product = this.coveoSearch?.state?.products?.filter(
          v => v.ec_product_id === value?.productNumber?.toString(),
        );
        const coveoProduct = {
          ...product[0],
          ec_price: Number(value?.price),
        };
        this.coveoSearch
          .interactiveProduct({
            options: {
              product: coveoProduct,
            } as any,
          } as any)
          .select();
      });

    this.queryParamUnsubscribe$ = queryParam$.subscribe(params => {
      this.setUpParams(params);
      this.summary?.state?.firstRequestExecuted &&
        this.parameterManagerController?.synchronize(
          this.convertParamsToCoveo(params),
        );
    });

    //move to base page?
    this.showInventory$ = this.userService
      .showMoxeInventory$()
      .subscribe(value => (this.showInventory = value));

    this.setMinWidthOperationButtons();
  }

  ionViewWillLeave(): void {
    this.isTouchExperience$?.unsubscribe();
    this.listPageDataSub$?.unsubscribe();
    this.queryParamUnsubscribe$?.unsubscribe();
    this.clearSearchSub$?.unsubscribe();
    this.isGuestUser$?.unsubscribe();
    this.showInventory$?.unsubscribe();
    this.coveoClickEventSub$?.unsubscribe();
    this.coveoTeardown();
    this.firstLoadPerformed = false;
    this.showInventory = undefined;
    this.coveoService.clearCustomContext();
  }

  openFacetFlyout(facetId?: string) {
    this.facetFlyout.openFlyout(facetId);
  }

  onActionButtonPressed() {
    if (this.selection === SelectionType.AddToList) {
      this.listsService.listsView$().subscribe(listData => {
        this.listsOverlayService.openAddToListModal(
          listData,
          this.selectedProducts,
        );
      });
    }
  }

  setMinWidthOperationButtons() {
    const showAddToListButton = !this.isGuestUser && !this.isInventory;
    const showInventoryButton =
      this.showInventory && !this.isGuestUser && !this.isAddToList;
    this.isThreeOperationButtons = showAddToListButton && showInventoryButton;
  }

  reloadResults() {
    const listingController = this.coveoSearch as ProductListing;
    listingController.refresh();
  }

  doUpdateTopSection(topSectionData: {
    bannerImageUrl: string;
    bannerTitle: string;
    infoTitle: string;
    additionalInfoText: string;
  }): void {
    this.isTopSection = true;
    this.topSectionBackgroundImageStyle = `url(${topSectionData.bannerImageUrl})`;
    this.topSectionBackgroundPosition = 'top center';
    this.topSectionImageTitle = topSectionData.bannerTitle;
    this.topSectionTextTitle = topSectionData.infoTitle;
    this.topSectionTextContent = topSectionData.additionalInfoText;
    this.isTopSectionTextExpanded =
      this.platformService.getPlatformType() !== PlatformEnum.mobile;
  }

  trackingLoadPage(productNumbers: any[]) {
    const searchTerm = this.route.snapshot.params['listingName'];
    this.newSearchService.trackSearchResultPlpPageLoad(
      productNumbers,
      searchTerm,
    );
  }

  trackingLoadMorePage(productNumbers: any[]) {
    const searchTerm = this.route.snapshot.params['listingName'];
    this.newSearchService.trackMoreSearchResultsPlpLoading(
      productNumbers,
      searchTerm,
    );
  }

  openAddToListModal() {
    this.addToListModalService.openModal(
      this.selectedProducts,
      false,
      false,
      this.route.snapshot.params['listingName'],
    );
    this.clearSearchSub$ = this.addToListModalService.clearSearch.subscribe(
      () => {
        this.clearSelectedProducts();
      },
    );
  }
}
